import { CookieIcon } from "lucide-react";
import {
  acceptAllCookies,
  openCookiePreferences,
  rejectAllCookies,
  useCookieConsent,
} from "@/core/lib/cookie-consent";
import { PRIVACY_URL } from "@/core/lib/marketing-url";
import { Button } from "../ui/button";

/**
 * First-visit consent banner. Deliberately has no dismiss "X": under GDPR,
 * closing without choosing can't be read as consent, so the only ways out are
 * Accept all, Reject all, or opening preferences.
 */
export function CookieBanner() {
  const { ready, needsConsent } = useCookieConsent();

  if (!ready || !needsConsent) return null;

  return (
    <div
      role="dialog"
      aria-live="polite"
      aria-label="Cookie consent"
      className="fixed inset-x-0 bottom-0 z-50 flex justify-center p-4 sm:p-6"
    >
      <div
        className="flex w-full max-w-3xl flex-col gap-4 rounded-xl border bg-popover p-4 text-popover-foreground
          shadow-xl ring-1 ring-foreground/5 sm:p-5 dark:ring-foreground/10
          animate-in fade-in-0 slide-in-from-bottom-4 duration-200"
      >
        <div className="flex gap-3">
          <span className="mt-0.5 hidden size-8 shrink-0 place-items-center rounded-lg bg-muted text-muted-foreground sm:grid">
            <CookieIcon className="size-4" />
          </span>

          <div className="flex flex-col gap-1">
            <p className="text-sm font-semibold">We use cookies</p>
            <p className="text-sm text-muted-foreground">
              Some are needed to sign you in and keep HR Docket running. Others are optional and
              only help us understand how the app is used. Read our{" "}
              <a
                href={PRIVACY_URL}
                target="_blank"
                rel="noreferrer"
                className="text-foreground underline underline-offset-4"
              >
                Privacy Policy
              </a>
              .
            </p>
          </div>
        </div>

        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:items-center sm:justify-end">
          <Button
            type="button"
            variant="ghost"
            className="sm:mr-auto"
            onClick={openCookiePreferences}
          >
            Manage preferences
          </Button>
          <Button type="button" variant="outline" onClick={rejectAllCookies}>
            Reject all
          </Button>
          <Button type="button" onClick={acceptAllCookies}>
            Accept all
          </Button>
        </div>
      </div>
    </div>
  );
}
